import clsx from 'clsx';

import styles from './StatTile.module.css';

import type { CSSProperties, ReactNode } from 'react';

export interface StatTileProps {
  readonly icon?: ReactNode;
  readonly label: string;
  readonly value: number | string;
  readonly caption?: string;
  /**
   * Drives the corner glow, the icon chip and the hover border through a single
   * custom property, so one card can never end up half-tinted.
   */
  readonly accent?: string;
  /**
   * `kpi` is the Command Center headline treatment (icon chip, 38px numeral,
   * hover lift); `stat` is the plain hero number used inside dense grids.
   */
  readonly variant?: 'stat' | 'kpi';
  /** Tints the caption only — the label always carries the meaning in text. */
  readonly captionTone?: string;
}

/**
 * A hero number. Deliberately not a chart — a single magnitude reads faster as
 * a numeral than as a one-bar plot.
 */
export function StatTile({
  icon,
  label,
  value,
  caption,
  accent,
  variant = 'stat',
  captionTone,
}: StatTileProps) {
  const isKpi = variant === 'kpi';

  return (
    <div
      className={clsx(styles.tile, isKpi && styles.kpi)}
      style={accent ? ({ '--kpc': accent } as CSSProperties) : undefined}
    >
      {/*
        KPI leads with its icon and label, then the numeral. The plain `stat`
        leads with the numeral, as in the prototype, so a grid of them reads as
        a row of figures rather than a row of headings.
      */}
      {isKpi && (
        <div className={styles.head}>
          {icon && (
            <span className={styles.iconChip} aria-hidden="true">
              {icon}
            </span>
          )}
          <span className={styles.label}>{label}</span>
        </div>
      )}

      <span className={styles.value} style={!isKpi && accent ? { color: accent } : undefined}>
        {value}
      </span>

      {!isKpi && <span className={styles.label}>{label}</span>}

      {caption && (
        <span className={styles.caption} style={captionTone ? { color: captionTone } : undefined}>
          {caption}
        </span>
      )}
    </div>
  );
}
